import { useCallback, useMemo } from 'react';
import { InfiniteData, InfiniteQueryObserverResult } from '@tanstack/react-query';
import { CellSpacing } from '@cbhq/cds-common';
import { Button, ButtonGroup } from '@cbhq/cds-web/buttons';
import { Icon } from '@cbhq/cds-web/icons';
import { Pictogram } from '@cbhq/cds-web/illustrations';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import {
  Table,
  TableBody,
  TableCell,
  TableCellFallback,
  TableFooter,
  TableHeader,
  TableRow,
} from '@cbhq/cds-web/tables';
import { TableCellSpacing } from '@cbhq/cds-web/tables/types/tableTypes';
import { TextBody, TextHeadline } from '@cbhq/cds-web/typography';

import { EventName, logClick, useLogViewOnMount } from ':cloud/init/clientAnalytics/logging';
import { PaymasterLogItem } from ':cloud/types/ts_types';
import { PAYMASTER_DOCS } from ':cloud/utils/links';
import { onExternalLinkPress } from ':cloud/utils/onExternalLinkPress';

import { formatLogDate } from '../helpers';

import { BUNDLER_ERROR_MAP, PAYMASTER_ERROR_MAP } from './constants';

const spacing: CellSpacing = { spacing: 1, spacingHorizontal: 5 };
const tableSpacing: TableCellSpacing = {
  outer: spacing,
  inner: spacing,
};

type PaymasterLogsTableProps = {
  onRowPress: (logIndex: number) => void;
  selectedLogIndex?: number;
  fetchNextPage: () => Promise<InfiniteQueryObserverResult<PaymasterLogItem[], unknown>>;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  hasNextPage: boolean;
  data: InfiniteData<PaymasterLogItem[]> | undefined;
  maxHeight?: number | string;
  handleRefreshPress: () => void;
};

export function PaymasterLogsTable({
  onRowPress,
  selectedLogIndex,
  fetchNextPage,
  isFetchingNextPage,
  hasNextPage,
  isLoading,
  data,
  maxHeight,
  handleRefreshPress,
}: PaymasterLogsTableProps) {
  const handleRowPress = useCallback(
    (index: number) => {
      return () => {
        onRowPress(index);
        logClick(EventName.paymaster_logs_table_row_click);
      };
    },
    [onRowPress],
  );

  useLogViewOnMount(EventName.paymaster_logs_table_view);

  const handleLoadMore = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  const tableBodyRows = useMemo(() => {
    return data?.pages.flatMap(
      (page) =>
        page?.map(({ id, createdAt, errorCode, method, service }, index) => {
          let error = '';
          if (service === 'Paymaster' && errorCode) {
            error = PAYMASTER_ERROR_MAP[errorCode];
          }
          if (service === 'Bundler' && errorCode) {
            error = BUNDLER_ERROR_MAP[errorCode];
          }

          const isSelected = selectedLogIndex === index;
          return (
            <TableRow
              key={id}
              onPress={handleRowPress(index)}
              backgroundColor={isSelected ? 'backgroundAlternate' : 'background'}
            >
              <TableCell title={formatLogDate(createdAt)} />
              <TableCell title={method || ''} />
              <TableCell>
                <HStack alignItems="center" gap={0.5}>
                  {error && <Icon name="dot" color="negative" size="s" />}
                  <TextBody as="p" color="foregroundMuted">
                    {error}
                  </TextBody>
                </HStack>
              </TableCell>
            </TableRow>
          );
        }),
    );
  }, [data?.pages, handleRowPress, selectedLogIndex]);

  const tableBodyContent = useMemo(() => {
    if (tableBodyRows?.length) {
      return tableBodyRows;
    }
    if (isLoading && !isFetchingNextPage) {
      return Array.from({ length: 10 }, (_, i) => (
        <TableRow key={i}>
          <TableCellFallback title width="100%" disableRandomRectWidth />
        </TableRow>
      ));
    }
    return (
      <TableRow>
        <TableCell colSpan={3} alignItems="center" justifyContent="center">
          <VStack alignItems="center" justifyContent="center" spacingTop={10} gap={3}>
            <Pictogram name="assetHubNavigation" />
            <TextHeadline as="p">There are no logs in this time range</TextHeadline>
            <ButtonGroup>
              <Button onPress={handleRefreshPress}>Refresh</Button>
              <Button variant="secondary" onPress={onExternalLinkPress(PAYMASTER_DOCS)}>
                Get started
              </Button>
            </ButtonGroup>
          </VStack>
        </TableCell>
      </TableRow>
    );
  }, [handleRefreshPress, isFetchingNextPage, isLoading, tableBodyRows]);

  return (
    <Box spacingVertical={3} flexGrow={1} overflow="scroll" height={maxHeight}>
      <Table cellSpacing={tableSpacing}>
        <TableHeader>
          <TableRow>
            <TableCell title="Created at" />
            <TableCell title="Method" />
            <TableCell title="Error" />
          </TableRow>
        </TableHeader>
        <TableBody>{tableBodyContent}</TableBody>
        {!!tableBodyRows?.length && hasNextPage && (
          <TableFooter>
            <TableRow>
              <TableCell colSpan={9} justifyContent="center" alignItems="center">
                <Button
                  variant="secondary"
                  compact
                  onPress={handleLoadMore}
                  loading={isFetchingNextPage}
                >
                  Load more
                </Button>
              </TableCell>
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </Box>
  );
}
